import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { productService } from "../../services/product.service";

interface CategoryStat {
  category: string;
  count: number;
  percentage: string;
}

export function CategoriesPage() {
  const [categoryStats, setCategoryStats] = useState<CategoryStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    loadCategoryStats();
  }, []);

  const loadCategoryStats = async () => {
    try {
      setLoading(true);
      const data = await productService.getCategoryStats();
      setCategoryStats(data);
    } catch (err: any) {
      setError(
        err.response?.data?.message || "Failed to load category statistics",
      );
    } finally {
      setLoading(false);
    }
  };

  // Filter categories based on search
  const filteredStats = categoryStats.filter((stat) =>
    stat.category.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Calculate total products
  const totalProducts = categoryStats.reduce(
    (sum, stat) => sum + stat.count,
    0,
  );

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Category Statistics
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          View product distribution across categories. Categories are now
          managed directly in products as text fields.
        </p>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-blue-400"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              Simplified Category Management
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>
                Categories are now stored as text fields directly in products
                for maximum flexibility. To add a new category, simply type it
                when creating or editing a product - no pre-configuration
                needed!
              </p>
              <Link
                to="/products/new"
                className="mt-2 inline-block font-medium underline hover:text-blue-900"
              >
                Create a new product →
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm font-medium text-gray-500">Total Products</p>
          <p className="text-2xl font-bold text-gray-900">
            {totalProducts.toLocaleString()}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm font-medium text-gray-500">Unique Categories</p>
          <p className="text-2xl font-bold text-gray-900">
            {categoryStats.length}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-sm font-medium text-gray-500">Top Category</p>
          <p className="text-2xl font-bold text-gray-900">
            {categoryStats[0]?.category || "N/A"}
          </p>
          <p className="text-sm text-gray-500">
            {categoryStats[0]
              ? `${categoryStats[0].count.toLocaleString()} products`
              : ""}
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <input
          type="text"
          placeholder="Search categories..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Categories Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent"></div>
            <p className="mt-2 text-gray-600">Loading category statistics...</p>
          </div>
        ) : filteredStats.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600">
              {searchTerm
                ? "No categories match your search"
                : "No categories found. Products without a category will appear once you add products."}
            </p>
            <Link
              to="/products/new"
              className="mt-4 inline-block px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Create your first product
            </Link>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Products
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Percentage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Distribution
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredStats.map((stat, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    {stat.category || "(No Category)"}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {stat.count.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {stat.percentage}%
                  </td>
                  <td className="px-6 py-4">
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div
                        className="bg-blue-600 h-2.5 rounded-full"
                        style={{
                          width: `${Math.min(parseFloat(stat.percentage), 100)}%`,
                        }}
                      ></div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right text-sm">
                    <Link
                      to={`/products?category=${encodeURIComponent(stat.category)}`}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      View Products
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
